import React from 'react';
import {
  ArrowRight,
  Building2,
  Wrench,
  Zap,
  Cpu,
  CheckCircle2,
  Handshake,
} from 'lucide-react';
import { Navbar } from '../../components/common/Navbar';
import { Footer } from '../../components/common/Footer';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { SectionHeader } from '../../components/common/SectionHeader';
import { SEO } from '../../components/common/SEO';

export default function PartnersPage() {
  const partnerGroups = [
    {
      title: '1. 建設会社・不動産・設備工事会社様',
      subtitle: '新築・改修時の省エネ・再エネ提案をスムーズに',
      icon: <Building2 className="w-6 h-6 text-spaq-cyan-dark" />,
      challenges: [
        '施主・オーナーから太陽光や蓄電池の相談を受けるが、需要に合った容量計算や収支試算に手間がかかる',
        '建物引き渡し後のデマンド抑制や空調連携まで含めた付加価値提案を行いたい',
      ],
      spaqRole: [
        '「1minute solar」を用いた初期段階での素早い概算・比較資料の作成支援',
        '30分デマンドデータに基づく詳細設計・機器選定・施工分担および「SPAQ CORE」の組み込み',
      ],
    },
    {
      title: '2. 太陽光・省エネ設備の販売店・施工事業者様',
      subtitle: '提案スピードの向上と、他社との差別化に',
      icon: <Wrench className="w-6 h-6 text-spaq-gold-dark" />,
      challenges: [
        '初回商談から提案書提出までに時間がかかり、検討熱度が下がってしまう',
        'パネルの価格競争になりがちで、「導入後の賢い運用（空調・蓄電池協調）」で差別化したい',
      ],
      spaqRole: [
        '「1minute solar」を活用した商談初期の相性診断・複数パターン比較の仕組み提供',
        '「SPAQ CORE」による既存デマコン・蓄電池協調モデルの共同提案・技術バックアップ',
      ],
    },
    {
      title: '3. 小売電気事業者・エネルギーサービス事業者様',
      subtitle: '需要家のピーク抑制と自家消費率向上をともに実現',
      icon: <Zap className="w-6 h-6 text-spaq-cyan-dark" />,
      challenges: [
        '高圧需要家向けに、単なる電気料金メニューだけでなくオンサイト太陽光・蓄電池制御を組み合わせたい',
        '需要家の負荷曲線を整え、市場調達リスクやピーク電力を抑える仕組みを探している',
      ],
      spaqRole: [
        '需要家ごとの負荷波形分析と、オンサイトPPA・自己所有太陽光の最適容量設計',
        'SPAQ COREを活用した現場主導のデマンドピーク抑制・将来の需給調整連携の共同検討',
      ],
    },
    {
      title: '4. 機器メーカー・システム開発事業者様',
      subtitle: 'デマコン・蓄電池・空調機器との接続・協調検証',
      icon: <Cpu className="w-6 h-6 text-navy" />,
      challenges: [
        '自社の蓄電池やデマンドコントローラー、計測機器を、より現場で使いやすい協調制御と組み合わせたい',
        '30分デマンド時限に同期した実践的な制御ロジックをフィールドで共同検証したい',
      ],
      spaqRole: [
        '通信プロトコル・抑止レジスタ等を用いた安全かつ柔軟な機器間連携の共同設計',
        '実フィールドおよびシミュレーション環境での協調制御シーケンス検証',
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white text-navy">
      <SEO
        title="協業・パートナーシップ｜共創で広げる電力の最適化"
        description="建設・設備工事業者様、電力小売・アグリゲーター様、機器メーカー様との協業について。SPAQのパートナーシップモデルをご紹介します。"
        canonicalPath="/partners"
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-hero-gradient border-b border-surface-border py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <Badge variant="cyan" className="mb-4">PARTNERSHIP</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy tracking-tight leading-tight">
              お客様の電力課題に、
              <br className="hidden sm:inline" />
              一緒に向き合う。
            </h1>
            <p className="mt-5 text-base sm:text-lg text-navy-muted leading-relaxed max-w-3xl">
              SPAQは、ソフトウェア（「1minute solar」）、現場制御（「SPAQ CORE」）、そして設計・施工の知見をオープンに組み合わせ、パートナー企業様それぞれの強みを活かした協業を推進しています。
            </p>
          </div>
        </section>

        {/* 4 Partner Categories */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              tag="COLLABORATION MODELS"
              title="パートナー様ごとの連携イメージと役割分担"
              subtitle="貴社の事業領域に合わせて、初期試算のみのご活用から、設計・施工・制御導入の共同推進まで柔軟に分担いたします。"
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
              {partnerGroups.map((group, idx) => (
                <div
                  key={idx}
                  className="rounded-3xl border border-surface-border bg-surface-slate/60 p-7 sm:p-8 shadow-soft flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center gap-3.5 mb-5">
                      <div className="w-12 h-12 rounded-2xl bg-white border border-surface-border flex items-center justify-center shrink-0">
                        {group.icon}
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-navy">{group.title}</h3>
                        <p className="text-xs font-semibold text-spaq-cyan-dark">
                          {group.subtitle}
                        </p>
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl p-4 border border-surface-border mb-4">
                      <div className="text-xs font-bold text-navy-muted mb-2">
                        よくあるご相談・課題
                      </div>
                      <ul className="space-y-1.5 text-xs sm:text-sm text-navy-muted">
                        {group.challenges.map((c, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-spaq-gold-dark font-bold">•</span>
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-surface-sky rounded-2xl p-4 border border-spaq-cyan/30">
                      <div className="text-xs font-bold text-spaq-cyan-dark mb-2">
                        SPAQが担える役割・提供価値
                      </div>
                      <ul className="space-y-2 text-xs sm:text-sm text-navy">
                        {group.spaqRole.map((r, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-0.5" />
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Flow of Partnership */}
        <section className="py-16 md:py-24 bg-surface-slate border-t border-surface-border">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              tag="HOW TO START"
              title="協業・連携スタートまでの流れ"
              align="center"
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-12">
              {[
                {
                  step: 'STEP 01',
                  title: 'オンラインでの情報交換',
                  desc: '貴社のお客様層や現在の提案フロー、課題感をヒアリングし、1minute solarやSPAQ COREのデモをご覧いただきます。',
                },
                {
                  step: 'STEP 02',
                  title: '役割分担・連携スキームの整理',
                  desc: '「初期試算」「現地調査・設計」「機器調達・施工」「制御導入」のどこを貴社・SPAQで分担するかを整理します。',
                },
                {
                  step: 'STEP 03',
                  title: '具体的な案件でのトライアル提案',
                  desc: '実際の検討案件（1〜2件）をもとに、1minute solarでの試算やデマンドデータ分析を共同で行い、提案の手応えを確認します。',
                },
                {
                  step: 'STEP 04',
                  title: '継続的なパートナーシップへ',
                  desc: '定型化されたスムーズな連携フローのもと、より多くの施設の電力最適化をともに推進します。',
                },
              ].map((s, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl border border-surface-border p-6 shadow-soft"
                >
                  <div className="text-xs font-mono font-bold text-spaq-cyan-dark mb-2">
                    {s.step}
                  </div>
                  <h3 className="text-base font-bold text-navy mb-2">{s.title}</h3>
                  <p className="text-xs text-navy-muted leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>

            <div className="rounded-3xl bg-navy text-white p-8 sm:p-10 text-center space-y-5 max-w-4xl mx-auto">
              <div className="w-12 h-12 rounded-2xl bg-white/10 text-spaq-cyan-bright flex items-center justify-center mx-auto">
                <Handshake className="w-6 h-6" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">
                まずは具体的な案件の壁打ちや、ツールのご紹介から。
              </h2>
              <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto">
                「いま相談を受けている施設の試算を一緒に見てほしい」「自社の営業チームで1minute solarを使ってみたい」など、お気軽にお声がけください。
              </p>
              <div className="pt-2">
                <Button
                  to="/contact?type=partner"
                  variant="primary"
                  size="lg"
                  icon={<ArrowRight className="w-4 h-4" />}
                >
                  協業・パートナー連携について相談する
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
